import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import FormError from "../../components/FormError";
import { Icons } from "../../components/icons";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { SheetFooter } from "../../components/ui/sheet";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "../../components/ui/use-toast";
import fetchData from "../../utils/api";
import Allergens from "./Allergens";
import NutritionalValues from "./NutritionalValues";
import ErrorBoundary from "../../ErrorBoundary";

export default function MenuItem({
  isOpen,
  setOpen,
  updateData,
  cartId,
  id,
  authToken,
  setCartTotal,
  restaurantDetail,
  isDineIn,
  selectedType,
  setSelectedType,
  orderingMethod,
  sizesOrderingMethod,
  couponValue = null,
  bogoFlow = false,
  bogoStep = 1,
  mealBundleFlow = false,
  mealBundleStep = 1,
  mealBundleTotalSteps = 1,
}) {
  const [is18Plus, setIs18Plus] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [initialValues, setInitialValues] = React.useState({
    size: updateData?.sizes.length > 0 ? updateData?.sizes[0].name : "",
    choices_and_addons: [] as any,
    quantity: 1,
    is_instructions: updateData?.specialInstructions
      ? updateData?.specialInstructions?.instructions?.includes("hide")
        ? false
        : true
      : true,
    instructions: "",
    cart_total: {
      size:
        updateData?.sizes.length > 0
          ? Number(updateData?.sizes[0].delivery_price)
          : Number(updateData?.deliveryPrice),
      choices_and_addons: [] as any,
      pricing: {
        pickup:
          updateData?.sizes.length > 0
            ? Number(updateData?.sizes[0].pickup_price)
            : Number(updateData?.pickupPrice),
        delivery:
          updateData?.sizes.length > 0
            ? Number(updateData?.sizes[0].delivery_price)
            : Number(updateData?.deliveryPrice),
        in_restaurant:
          updateData?.sizes.length > 0
            ? Number(updateData?.sizes[0].dine_in_price)
            : Number(updateData?.dineInPrice),
      },
    },
  });

  React.useEffect(() => {
    if (!updateData) return;
    setIs18Plus(updateData?.majorGroup === "Alcoholic Beverage" ? false : true);

    const getUpdatedSize = () =>
      updateData.sizes?.find((size) => size.pre_selected) ||
      updateData.sizes?.[0];

    const updatedSize = getUpdatedSize();

    const calculateCartTotal = () => ({
      size: updatedSize
        ? updatedSize.delivery_price
        : Number(updateData?.deliveryPrice || 0),
      choices_and_addons: [],
      pricing: {
        pickup: updatedSize
          ? Number(updatedSize.pickup_price)
          : Number(updateData?.pickupPrice || 0),
        delivery: updatedSize
          ? Number(updatedSize.delivery_price)
          : Number(updateData?.deliveryPrice || 0),
        in_restaurant: updatedSize
          ? Number(updatedSize.dine_in_price)
          : Number(updateData?.dineInPrice || 0),
      },
    });

    const defaultInitialValues = {
      size: updatedSize?.name || "",
      choices_and_addons: [],
      choices_and_addons_group: [],
      quantity: 1,
      is_instructions: updateData.specialInstructions
        ? !updateData.specialInstructions.instructions?.includes("hide")
        : true,
      instructions: "",
      cart_total: calculateCartTotal(),
    };

    // If there are no container orders, set the initial values and exit
    if (!updateData.choicesAndAddons?.containerOrder?.length) {
      setInitialValues(defaultInitialValues);
      return;
    }

    // Process container orders
    const newInitialValues: any = { ...defaultInitialValues };

    updateData.choicesAndAddons.containerOrder.forEach((containerId) => {
      const container =
        updateData.choicesAndAddons.containers?.[containerId.id];
      if (!container) return;

      const mergedArray = Array.from(
        new Set([
          ...(updateData?.categoryChoicesAndAddons || []),
          ...(updateData?.choicesAndAddonsRaw || []),
          ...(updatedSize?.choices_and_add_on?.filter(Boolean) || []),
        ])
      );

      // Skip containers not present in the merged array
      if (!mergedArray.includes(container.title)) return;

      newInitialValues.choices_and_addons_group = mergedArray;

      container.items?.forEach((item) => {
        const {
          availability,
          name,
          categories,
          choicesPreSelected,
          choicesAndAddonsGroupDetails,
        } = item;

        const updatedCategoryName = categories.replace(/[ .]/g, "_");

        if (availability === "in_stock" && choicesPreSelected) {
          if (!newInitialValues.choices_and_addons.includes(name)) {
            newInitialValues.choices_and_addons.push(name);
            newInitialValues.cart_total.choices_and_addons.push({
              name,
              price: Number(item.deliveryPrice),
              pricing: {
                pickup: Number(item.pickupPrice),
                delivery: Number(item.deliveryPrice),
                in_restaurant: Number(item.dineInPrice),
              },
            });
            if (
              choicesAndAddonsGroupDetails &&
              choicesAndAddonsGroupDetails.choice === "mandatory"
            ) {
              const groupKey = `choices_group_${updatedCategoryName}`;
              newInitialValues[groupKey] = [
                ...(newInitialValues[groupKey] || []),
                name,
              ];
            }
          }
        }

        // Handle mandatory choices group
        if (
          choicesAndAddonsGroupDetails &&
          choicesAndAddonsGroupDetails.choice === "mandatory"
        ) {
          const groupKey = `choices_group_${updatedCategoryName}`;
          newInitialValues[groupKey] = [
            ...(newInitialValues[groupKey] || []),
            // name,
          ];
        }
        if (
          choicesAndAddonsGroupDetails &&
          choicesAndAddonsGroupDetails.choice !== "mandatory"
        ) {
          const groupKey = `choices_group_${updatedCategoryName}`;
          newInitialValues[groupKey] = [
            ...(newInitialValues[groupKey] || []),
            // name,
          ];
        }
      });
    });

    setInitialValues(newInitialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateData]);

  const { t } = useTranslation();

  const capitalizeFirstLetter = (string) => {
    return string
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const validationSchema = () => {
    const updatedSize =
      updateData?.sizes?.length > 0
        ? updateData?.sizes.find((size) => size.pre_selected) ||
          updateData?.sizes[0]
        : null;

    const updatedSchema: any = {};
    if (updateData?.sizes.length > 0) {
      updatedSchema.size = Yup.string().required(
        "menu.form.validation.size_required"
      );
    }

    if (updateData?.choicesAndAddons?.containerOrder?.length > 0) {
      updateData?.choicesAndAddons?.containerOrder?.forEach((containerId) => {
        const container =
          updateData?.choicesAndAddons.containers?.[containerId.id];
        const categoryChoicesAndAddons =
          updateData?.categoryChoicesAndAddons || [];
        const choicesAndAddonsRaw = updateData?.choicesAndAddonsRaw || [];

        // Merge arrays to determine applicable choices and add-ons
        const mergedArray = updatedSize
          ? Array.from(
              new Set([
                ...categoryChoicesAndAddons,
                ...choicesAndAddonsRaw,
                ...(updateData?.sizes
                  ?.find((size) => size.name === updatedSize.name)
                  ?.choices_and_add_on?.filter(Boolean) || []),
              ])
            )
          : Array.from(
              new Set([...categoryChoicesAndAddons, ...choicesAndAddonsRaw])
            );

        // Skip processing if the container title is not in the merged array
        if (!mergedArray.includes(container?.title)) {
          return;
        }

        // Iterate through container items
        container?.items?.forEach((item) => {
          const groupName = item.categories.replace(/[ .]/g, "_");
          const groupNameRaw = item.categories;
          const choicesAndAddonsGroupDetails =
            item.choicesAndAddonsGroupDetails;
          if (
            Object.keys(choicesAndAddonsGroupDetails).length > 0 &&
            choicesAndAddonsGroupDetails.choice === "mandatory"
          ) {
            updatedSchema[`choices_group_${groupName}`] = Yup.array()
              .required(
                `${t("menu.form.validation.min_choice")} ${Number(
                  choicesAndAddonsGroupDetails.force_minimum
                )} ${groupNameRaw}`
              )
              .min(
                Number(choicesAndAddonsGroupDetails.force_minimum),
                `${t("menu.form.validation.min_choice")} ${Number(
                  choicesAndAddonsGroupDetails.force_minimum
                )} ${groupNameRaw}`
              )
              .max(
                Number(choicesAndAddonsGroupDetails.force_maximum),
                `${t("menu.form.validation.max_choice")} ${Number(
                  choicesAndAddonsGroupDetails.force_maximum
                )} ${groupNameRaw}`
              );
          }
        });
      });
    }
    return Yup.object().shape(updatedSchema);
  };

  const getSelectedItemsCount = (values: any, title: string) => {
    return values[`choices_group_${title}`]?.length || 0;
  };

  const isMaximumReached = (
    values: any,
    title: string,
    forceMaximum: number
  ) => {
    const selectedCount = getSelectedItemsCount(values, title);
    return selectedCount >= forceMaximum;
  };

  return (
    <ErrorBoundary>
      <Dialog
        open={isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOpen(isOpen, false, null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px] max-h-fit">
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema()}
            onSubmit={async (values) => {
              setIsLoading(true);
              interface OrderDetails {
                id: string;
                name: string;
                categories: string;
                taxation_category?: string;
                timing: any;
                bogoPromo?: any;
                bogoStep?: number;
                mealBundlePromo?: any;
                mealBundleStep?: number;
                couponValue?: any;
                promoId?: string;
                promoDiscount?: any;
                firstItemInfo?: any;
                [key: string]: any;
              }
              const details: OrderDetails = {
                id: updateData.id,
                name: updateData.name,
                categories: updateData.categories,
                couponValue: couponValue,
                taxation_category: updateData?.taxationCategory,
                timing: updateData?.timings,
                ...values,
              };
              if (bogoFlow) {
                details.bogoPromo = updateData.bogoPromo;
                details.bogoStep = updateData.bogoStep;
                details.promoId = updateData.promoId;

                if (bogoStep === 2) {
                  details.promoDiscount = updateData.promoDiscount;
                  details.firstItemInfo = updateData.firstItemInfo;
                }
              }
              if (mealBundleFlow) {
                details.mealBundlePromo = updateData.mealBundlePromo;
                details.mealBundleStep = updateData.mealBundleStep;
                details.promoId = updateData.promoId;

                if (mealBundleStep === mealBundleTotalSteps) {
                  details.promoDiscount = updateData.promoDiscount;
                }
              }

              try {
                const body: any = {
                  cart_id: cartId,
                  values: details,
                };
                const res = await fetchData({
                  url: "cart/insert",
                  method: "POST",
                  body,
                  token: authToken,
                });
                if (res.status_code === 200) {
                  setIsLoading(false);
                  const itemName =
                    details.name + (values.size ? ` (${values.size})` : "");
                  setOpen(false, true, itemName);
                  setCartTotal(res.data.cart_total);
                  toast({
                    title: t("success"),
                    description: t(res.message),
                  });
                }
              } catch (error) {
                console.log("error", error);
              }
            }}
            validateOnChange={true}
            validateOnBlur={true}
            validateOnMount={true}
          >
            {({ errors, touched, setFieldValue, values, handleSubmit }) => {
              let count;
              let countTotal;
              if (selectedType === "dine_in") {
                count =
                  values.cart_total.pricing.in_restaurant +
                  values.cart_total.choices_and_addons
                    .map((item) => item.pricing.in_restaurant)
                    .reduce((a, b) => a + b, 0);
                countTotal = count * values.quantity;
              } else {
                count =
                  values.cart_total.pricing[selectedType] +
                  values.cart_total.choices_and_addons
                    .map((item) => item.pricing[selectedType])
                    .reduce((a, b) => a + b, 0);
                countTotal = count * values.quantity;
              }

              return (
                <form
                  className="max-h-[calc(100vh-9rem)] flex flex-col justify-between gap-5"
                  onSubmit={handleSubmit}
                >
                  <DialogHeader>
                    <DialogTitle>{updateData?.name}</DialogTitle>
                    <DialogDescription>
                      {updateData?.itemDescription}
                    </DialogDescription>
                    <DialogDescription>
                      {updateData?.tags
                        ? JSON.parse(updateData?.tags).join(", ")
                        : ""}
                    </DialogDescription>
                  </DialogHeader>
                  {updateData?.majorGroup === "Alcoholic Beverage" && (
                    <div>
                      <div className="grid gap-2">
                        <label
                          className={`block ${
                            isLoading
                              ? "cursor-not-allowed opacity-70"
                              : "cursor-pointer"
                          }`}
                          htmlFor={"am_18_years_old"}
                        >
                          <Card className="p-3">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                disabled={isLoading}
                                id={"am_18_years_old"}
                                checked={is18Plus === true}
                                onCheckedChange={(e) => {
                                  setIs18Plus(e === true);
                                }}
                              />
                              <div className="flex items-center justify-between w-full">
                                <div>
                                  <p className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 self-center">
                                    {t("menu.am_18_years_old")}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="p-1 max-h-full h-full overflow-y-auto custom-scrollbar">
                    {updateData?.sizes?.length > 0 && (
                      <div className="grid gap-2">
                        <div>
                          <Label className="">
                            {t("menu.sizes")}
                            <Label className="opacity-70">
                              {" "}
                              ({t("menu.required")})
                            </Label>
                          </Label>
                        </div>
                        <div className="grid gap-2">
                          {updateData?.sizes.map((item, index) => {
                            if (item[sizesOrderingMethod] === "0.00") {
                              return null;
                            }
                            return (
                              <label
                                className={`block ${
                                  isLoading
                                    ? "cursor-not-allowed opacity-70"
                                    : "cursor-pointer"
                                }`}
                                htmlFor={item.name}
                                key={index}
                              >
                                <Card className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      disabled={isLoading}
                                      id={item.name}
                                      checked={values.size === item.name}
                                      onCheckedChange={(e) => {
                                        setFieldValue("size", item.name);
                                        setFieldValue("choices_and_addons", []);
                                        setFieldValue("cart_total", {
                                          size: Number(item.delivery_price),
                                          choices_and_addons: [],
                                          pricing: {
                                            pickup: Number(item.pickup_price),
                                            delivery: Number(
                                              item.delivery_price
                                            ),
                                            in_restaurant: Number(
                                              item.dine_in_price
                                            ),
                                          },
                                        });
                                      }}
                                    />
                                    <div className="flex items-center justify-between w-full">
                                      <div>
                                        <p className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 self-center">
                                          {t(item.name)}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 self-center">
                                          {selectedType !== "" && (
                                            <React.Fragment>
                                              {
                                                restaurantDetail
                                                  ?.currencyDetails?.symbol
                                              }{" "}
                                              {isDineIn
                                                ? t(item.dine_in_price)
                                                : t(item[sizesOrderingMethod])}
                                            </React.Fragment>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              </label>
                            );
                          })}
                          {errors.size && touched.size ? (
                            <FormError msg={t(errors.size.toString())} />
                          ) : null}
                        </div>
                      </div>
                    )}
                    {updateData?.choicesAndAddons?.containerOrder?.length > 0 &&
                      updateData?.choicesAndAddons?.containerOrder?.map(
                        (containerId, index) => {
                          const container =
                            updateData?.choicesAndAddons?.containers[
                              containerId.id
                            ];

                          const categoryChoicesAndAddons =
                            updateData?.categoryChoicesAndAddons;

                          const choicesAndAddonsRaw =
                            updateData?.choicesAndAddonsRaw;
                          let mergedArray;
                          if (values?.size !== "") {
                            const sizeObject =
                              updateData?.sizes
                                ?.find((size) => size.name === values?.size)
                                ?.choices_and_add_on?.filter(
                                  (choice) => choice != null
                                ) || [];

                            mergedArray = Array.from(
                              new Set([
                                ...categoryChoicesAndAddons,
                                ...choicesAndAddonsRaw,
                                ...sizeObject,
                              ])
                            );

                            if (!mergedArray.includes(container?.title)) {
                              return null;
                            }
                          } else {
                            mergedArray = Array.from(
                              new Set([
                                ...categoryChoicesAndAddons,
                                ...choicesAndAddonsRaw,
                              ])
                            );
                            if (!mergedArray.includes(container?.title)) {
                              return null;
                            }
                          }
                          const title = container?.title?.replace(/[ .]/g, "_");

                          return (
                            <div className="grid gap-2" key={index}>
                              <div>
                                <Label className="">{container?.title}</Label>
                              </div>
                              {container.items.map((item, index) => {
                                if (item.visibility === 0) return null;
                                const forceMaximum =
                                  item.choicesAndAddonsGroupDetails?.choice ===
                                  "mandatory"
                                    ? item.choicesAndAddonsGroupDetails
                                        ?.force_maximum
                                    : 999;
                                const maxReached = isMaximumReached(
                                  values,
                                  title,
                                  forceMaximum
                                );
                                const isChecked =
                                  values.choices_and_addons.includes(
                                    item.name
                                  ) &&
                                  values[`choices_group_${title}`]?.includes(
                                    item.name
                                  );
                                const isDisabled =
                                  isLoading ||
                                  item.availability !== "in_stock" ||
                                  (maxReached && !isChecked);

                                // if (item[orderingMethod] === "0.00") {
                                //   return null;
                                // }

                                if (item.availability !== "in_stock") {
                                  return null;
                                }

                                return (
                                  <label
                                    className={`block ${
                                      isLoading ||
                                      item.availability !== "in_stock"
                                        ? "cursor-not-allowed opacity-70"
                                        : "cursor-pointer"
                                    }`}
                                    htmlFor={item.name}
                                    key={index}
                                  >
                                    <Card
                                      className={`p-3 ${
                                        item.availability === "in_stock"
                                          ? "cursor-pointer"
                                          : "cursor-not-allowed"
                                      }`}
                                      key={index}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          className={`${
                                            !isDisabled
                                              ? "cursor-pointer"
                                              : "cursor-not-allowed"
                                          }`}
                                          disabled={isDisabled}
                                          id={item.name}
                                          checked={isChecked}
                                          onCheckedChange={(checked: any) => {
                                            if (
                                              item.availability ===
                                                "in_stock" &&
                                              (!maxReached ||
                                                (maxReached && !checked))
                                            ) {
                                              if (checked) {
                                                setFieldValue(
                                                  "choices_and_addons",
                                                  [
                                                    ...values.choices_and_addons,
                                                    item.name,
                                                  ]
                                                );
                                                setFieldValue("cart_total", {
                                                  size: values.cart_total.size,
                                                  pricing:
                                                    values.cart_total.pricing,
                                                  choices_and_addons: [
                                                    ...values.cart_total
                                                      .choices_and_addons,
                                                    {
                                                      name: item.name,
                                                      price: Number(
                                                        item.deliveryPrice
                                                      ),
                                                      pricing: {
                                                        pickup: Number(
                                                          item.pickupPrice
                                                        ),
                                                        delivery: Number(
                                                          item.deliveryPrice
                                                        ),
                                                        in_restaurant: Number(
                                                          item.dineInPrice
                                                        ),
                                                      },
                                                    },
                                                  ],
                                                });
                                                setFieldValue(
                                                  `choices_group_${title}`,
                                                  [
                                                    ...values[
                                                      `choices_group_${title}`
                                                    ],
                                                    item.name,
                                                  ]
                                                );
                                              } else {
                                                setFieldValue(
                                                  "choices_and_addons",
                                                  values.choices_and_addons.filter(
                                                    (value) =>
                                                      value !== item.name
                                                  )
                                                );
                                                setFieldValue("cart_total", {
                                                  size: values.cart_total.size,
                                                  pricing:
                                                    values.cart_total.pricing,
                                                  choices_and_addons:
                                                    values.cart_total.choices_and_addons.filter(
                                                      (value) =>
                                                        value.name !== item.name
                                                    ),
                                                });
                                                setFieldValue(
                                                  `choices_group_${title}`,
                                                  values[
                                                    `choices_group_${title}`
                                                  ].filter(
                                                    (value) =>
                                                      value !== item.name
                                                  )
                                                );
                                              }
                                            }
                                          }}
                                        />
                                        <div className="flex items-center justify-between w-full">
                                          <div>
                                            <p className="text-sm font-medium leading-none">
                                              {t(item.name)}{" "}
                                              {item.availability !==
                                                "in_stock" && (
                                                <Badge variant="destructive">
                                                  {t("out_of_stock")}
                                                </Badge>
                                              )}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium leading-none">
                                              {selectedType !== "" && (
                                                <React.Fragment>
                                                  {
                                                    restaurantDetail
                                                      ?.currencyDetails?.symbol
                                                  }{" "}
                                                  {isDineIn
                                                    ? t(item.dineInPrice)
                                                    : t(item[orderingMethod])}
                                                </React.Fragment>
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </Card>
                                  </label>
                                );
                              })}
                              {errors[`choices_group_${title}`] &&
                              touched[`choices_group_${title}`] ? (
                                <FormError
                                  msg={t(errors[`choices_group_${title}`])}
                                />
                              ) : null}
                            </div>
                          );
                        }
                      )}
                    {values.is_instructions && (
                      <div className="grid gap-2">
                        <div>
                          <Label htmlFor="instructions" className="">
                            {t("menu.special_instructions")}
                          </Label>
                        </div>
                        <div>
                          <div className="flex gap-2">
                            <Textarea
                              id="instructions"
                              placeholder={t(
                                "menu.special_instructions_placeholder"
                              )}
                              value={values.instructions}
                              onChange={(e: any) => {
                                setFieldValue(
                                  "instructions",
                                  e.target.value,
                                  true
                                );
                              }}
                              disabled={isLoading}
                              maxLength={200}
                            />
                          </div>
                          {errors.instructions && touched.instructions ? (
                            <FormError msg={t(errors.instructions)} />
                          ) : null}
                        </div>
                      </div>
                    )}
                    <div className="grid gap-2">
                      <div>
                        <Label htmlFor="name" className="">
                          {t("menu.quantity")}
                        </Label>
                      </div>
                      <div>
                        <div className="flex items-center justify-center">
                          <Button
                            type="button"
                            className="w-10 h-10 rounded-full"
                            onClick={() => {
                              setFieldValue(
                                "quantity",
                                values.quantity - 1,
                                true
                              );
                            }}
                            disabled={values.quantity == 1 || isLoading}
                          >
                            -
                          </Button>
                          <Input
                            type="number"
                            min={1}
                            max={50}
                            id="quantity"
                            placeholder={t("menu.quantity_placeholder")}
                            value={values.quantity}
                            onChange={(e: any) => {
                              // This clamps the value between 1 and 50
                              const value = Math.min(
                                50,
                                Math.max(1, parseInt(e.target.value, 10) || 0)
                              );
                              setFieldValue("quantity", value, true);
                            }}
                            disabled={isLoading}
                            className="mx-2 text-center"
                          />
                          <Button
                            type="button"
                            className="w-10 h-10 rounded-full"
                            onClick={() => {
                              setFieldValue(
                                "quantity",
                                values.quantity + 1,
                                true
                              );
                            }}
                            disabled={values.quantity == 50 || isLoading}
                          >
                            +
                          </Button>
                        </div>
                        {errors.quantity && touched.quantity ? (
                          <FormError msg={t(errors.quantity as string)} />
                        ) : null}
                      </div>
                    </div>
                    <div className="space-y-2 divide-y ">
                      {updateData &&
                        updateData.ingredients &&
                        updateData.ingredients !== "" && (
                          <div className="space-y-2 ">
                            <Label htmlFor="ingredients" className="text-left">
                              {t("menu.ingredients")}:
                            </Label>
                            <div>
                              <Badge
                                className="p-2 text-sm bg-accent"
                                variant="outline"
                              >
                                {updateData?.ingredients}
                              </Badge>
                            </div>
                          </div>
                        )}
                      <Allergens updateData={updateData} t={t} />

                      {updateData &&
                        updateData.additives &&
                        updateData.additives !== "" && (
                          <div className="space-y-2 pt-5">
                            <Label htmlFor="ingredients" className="text-left">
                              {t("menu.additives")}:
                            </Label>
                            <div>
                              <Badge
                                className="p-2 text-sm bg-accent"
                                variant="outline"
                              >
                                {updateData?.additives}
                              </Badge>
                            </div>
                          </div>
                        )}
                      <NutritionalValues
                        updateData={updateData}
                        t={t}
                        capitalizeFirstLetter={capitalizeFirstLetter}
                      />
                    </div>
                  </div>
                  <SheetFooter>
                    <div className="flex justify-center w-full">
                      <Button
                        disabled={
                          (is18Plus ? false : true) ||
                          (selectedType === ""
                            ? true
                            : isLoading
                            ? true
                            : false)
                        }
                        type="submit"
                        className="w-full"
                      >
                        {isLoading && (
                          <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        <div className="flex w-full justify-between">
                          <div>
                            <Label>
                              {selectedType !== "" && (
                                <React.Fragment>
                                  {restaurantDetail?.currencyDetails?.symbol}{" "}
                                  {countTotal?.toFixed(2)}
                                </React.Fragment>
                              )}
                            </Label>
                          </div>
                          <div>
                            <Label>{t("menu.add_to_cart")}</Label>
                          </div>
                        </div>
                      </Button>
                    </div>
                  </SheetFooter>
                </form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}
